// Page Panier - Design Licorne Premium
// Parcours achat optimisé

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight,
  Truck, Shield, CreditCard, Gift, Tag, ChevronRight, X,
  Package, Clock, MapPin, AlertTriangle, Sparkles, Check, Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { useBuyer } from '../../contexts/BuyerContext';

export default function PanierPage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const {
    cart,
    cartSubtotal,
    shippingCost,
    cartTotal,
    cartCount,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    isAuthenticated
  } = useBuyer();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price || 0);
  };

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;
    
    // Simulation de codes promo
    const promoCodes = {
      'BIENVENUE10': 10,
      'FONDATEUR20': 20,
      'FLASH15': 15
    };

    const discountPercent = promoCodes[promoCode.toUpperCase()];
    if (discountPercent) {
      const discountAmount = (cartSubtotal * discountPercent) / 100;
      setDiscount(discountAmount);
      setPromoApplied(true);
      toast.success(`Code promo appliqué : -${discountPercent}%`);
    } else {
      toast.error('Code promo invalide');
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toast.info('Connecte-toi pour finaliser ta commande');
      navigate('/connexion-acheteur?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  // Panier vide
  if (cart.length === 0) {
    // Version mobile - Épurée et centrée
    if (isMobile) {
      return (
        <div className="fixed inset-0 z-[10002] bg-[#F8FAFC] flex flex-col">
          {/* Header simple mobile */}
          <header 
            className="flex items-center gap-3 px-4 bg-white border-b border-gray-100"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}
          >
            <button onClick={() => navigate(-1)} className="p-1">
              <ArrowLeft size={22} className="text-gray-700" />
            </button>
            <h1 className="text-[16px] font-bold text-gray-900">Mon Panier</h1>
          </header>

          {/* Contenu centré */}
          <div className="flex-1 flex items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-6"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
                <ShoppingCart size={40} className="text-gray-300" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Ton panier est vide</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Explore notre marketplace pour découvrir des milliers de produits professionnels aux meilleurs prix.
                </p>
              </div>
              
              {/* 3 boutons d'action */}
              <div className="space-y-3 pt-2">
                <Link to="/artisans" className="block">
                  <Button className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white px-6 py-3 text-sm rounded-xl">
                    Trouve un artisan
                  </Button>
                </Link>
                <Link to="/produits" className="block">
                  <Button className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 text-sm rounded-xl">
                    Achète à un pro
                  </Button>
                </Link>
                <Link to="/entre-particuliers" className="block">
                  <Button variant="outline" className="w-full border-2 border-[#F97316] text-[#F97316] hover:bg-orange-50 px-6 py-3 text-sm rounded-xl">
                    Entre particuliers
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }

    // Version desktop - Avec navigation
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />

        <main className="max-w-4xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="w-24 h-24 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
              <ShoppingCart size={48} className="text-gray-300" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Ton panier est vide</h1>
            <p className="text-gray-500 max-w-md mx-auto">
              Explore notre marketplace pour découvrir des milliers de produits professionnels aux meilleurs prix.
            </p>
            <Link to="/produits">
              <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white px-8 py-6 text-lg rounded-xl">
                <Package size={20} className="mr-2" />
                Découvrir les produits
              </Button>
            </Link>
          </motion.div>

          {/* Suggestions - Desktop uniquement */}
          <div className="mt-16">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Peut-être que tu cherches...</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {['Outillage', 'Électricité', 'Plomberie', 'Peinture', 'Menuiserie'].map((cat) => (
                <Link 
                  key={cat}
                  to={`/produits?categorie=${cat.toLowerCase()}`}
                  className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:border-[#F97316] hover:text-[#F97316] transition-colors"
                >
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header de la page */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mon Panier</h1>
                <p className="text-sm text-gray-500">{cartCount} article{cartCount > 1 ? 's' : ''}</p>
              </div>
            </div>
            {cart.length > 0 && (
              <button 
                onClick={() => {
                  if (confirm('Vider le panier ?')) clearCart();
                }}
                className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1"
              >
                <Trash2 size={16} />
                Vider
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Colonne Articles */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Alerte mode préouverture */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Les paiements ne sont pas encore actifs. Explore le parcours achat !
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Liste des articles */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-100">
                <AnimatePresence mode="popLayout">
                  {cart.map((item, index) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20, height: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex gap-4">
                        {/* Image */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-xl bg-gray-100 overflow-hidden">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={32} className="text-gray-300" />
                            </div>
                          )}
                        </div>

                        {/* Infos */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                              <p className="text-sm text-gray-500 mt-0.5">
                                Vendu par {typeof item.seller_name === 'object' ? item.seller_name?.name : (item.seller_name || 'AbracadaBati')}
                              </p>
                            </div>
                            <button 
                              onClick={() => removeFromCart(item.id)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <X size={18} />
                            </button>
                          </div>

                          <div className="flex items-end justify-between mt-3">
                            {/* Prix */}
                            <div>
                              <span className="text-lg font-bold text-[#F97316]">
                                {formatPrice(item.price * item.quantity)}
                              </span>
                              {item.quantity > 1 && (
                                <span className="text-xs text-gray-400 ml-2">
                                  ({formatPrice(item.price)} / unité)
                                </span>
                              )}
                            </div>

                            {/* Quantité */}
                            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
                              <button 
                                onClick={() => updateCartQuantity(item.id, -1)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white transition-colors"
                              >
                                <Minus size={16} />
                              </button>
                              <span className="w-8 text-center font-medium">{item.quantity}</span>
                              <button 
                                onClick={() => updateCartQuantity(item.id, 1)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white transition-colors"
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Avantages */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Truck, label: 'Livraison offerte dès 50€', color: 'emerald' },
                { icon: Shield, label: 'Paiement 100% sécurisé', color: 'blue' },
                { icon: Clock, label: 'Retour sous 14 jours', color: 'purple' }
              ].map((item, idx) => (
                <div key={idx} className="bg-white rounded-xl p-3 text-center border border-gray-100">
                  <item.icon size={20} className={`mx-auto text-${item.color}-500 mb-1`} />
                  <p className="text-xs text-gray-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Colonne Récapitulatif */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-4 space-y-6">
              <h2 className="font-bold text-lg text-gray-900">Récapitulatif</h2>

              {/* Code promo */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Code promo</label>
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="BIENVENUE10"
                    disabled={promoApplied}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleApplyPromo}
                    disabled={promoApplied}
                    variant="outline"
                    className="shrink-0"
                  >
                    {promoApplied ? <Check size={16} /> : 'OK'}
                  </Button>
                </div>
                {promoApplied && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <Check size={12} />
                    Code appliqué : {promoCode.toUpperCase()}
                  </p>
                )}
              </div>

              {/* Détails prix */}
              <div className="space-y-3 py-4 border-y border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total ({cartCount} article{cartCount > 1 ? 's' : ''})</span>
                  <span className="font-medium">{formatPrice(cartSubtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Livraison</span>
                  <span className={`font-medium ${shippingCost === 0 ? 'text-emerald-600' : ''}`}>
                    {shippingCost === 0 ? 'Offerte' : formatPrice(shippingCost)}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Réduction</span>
                    <span className="font-medium text-emerald-600">-{formatPrice(discount)}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="flex justify-between items-baseline">
                <span className="font-bold text-gray-900">Total</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#F97316]">
                    {formatPrice(cartTotal - discount)}
                  </span>
                  <p className="text-xs text-gray-500">TTC</p>
                </div>
              </div>

              {/* Bouton Commander */}
              <Button 
                onClick={handleCheckout}
                className="w-full h-14 bg-[#F97316] hover:bg-[#EA580C] text-white text-lg font-semibold rounded-xl"
                data-testid="btn-checkout"
              >
                Commander
                <ArrowRight size={20} className="ml-2" />
              </Button>

              {/* Infos sécurité */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <Shield size={14} />
                <span>Paiement sécurisé par Stripe</span>
              </div>

              {/* Continuer les achats */}
              <Link 
                to="/produits"
                className="block text-center text-sm text-[#F97316] hover:underline"
              >
                Continuer mes achats
              </Link>
            </div>
          </div>
        </div>
      </main>

      
      <div className="h-24 lg:hidden" />
    </div>
  );
}
